import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { adminUsersApi, partnersApi } from '../lib/api';
import { Header } from '../components/Header';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '../components/ui/sheet';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
    Search, 
    Users, 
    Shield, 
    User, 
    Languages, 
    Filter,
    Clock,
    Mail,
    Calendar,
    Hash,
    AlertTriangle,
    History,
    Building2,
    CheckCircle2,
    ExternalLink,
    Briefcase,
    Plus,
    Eye,
    EyeOff,
    X,
    Loader2,
    Star,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
} from 'lucide-react';

// Role badge component
const RoleBadge = ({ role }) => {
    const styles = {
        admin: 'bg-indigo-100 text-indigo-700',
        customer: 'bg-slate-100 text-slate-700',
        translator: 'bg-purple-100 text-purple-700',
        repro_partner: 'bg-blue-100 text-blue-700',
    };
    
    const icons = {
        admin: Shield,
        customer: User,
        translator: Languages,
        repro_partner: Briefcase,
    };
    
    const labels = {
        admin: 'Admin',
        customer: 'Customer',
        translator: 'Translator',
        repro_partner: 'Repro Partner',
    };
    
    const Icon = icons[role] || User;
    
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${styles[role] || 'bg-slate-100 text-slate-700'}`}>
            <Icon className="h-3 w-3" />
            {labels[role] || role}
        </span>
    );
};

// Status badge component
const StatusBadge = ({ isActive }) => {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
        }`}>
            {isActive ? 'Active' : 'Disabled'}
        </span>
    );
};

export default function UsersPage({ embedded = false } = {}) {
    const { t } = useTranslation();
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    // Sort state for the users table. ``sortBy`` is the column key
    // (``name`` | ``email`` | ``role`` | ``is_active`` | ``last_login``
    // | ``organization_name`` | ``created_at``), ``sortDir`` is
    // 'asc'|'desc'. Default: most recent sign-in on top, matching
    // the "who's active" admin instinct.
    const [sortBy, setSortBy] = useState('last_login');
    const [sortDir, setSortDir] = useState('desc');
    
    // Edit drawer state
    const [selectedUser, setSelectedUser] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        role: '',
        is_active: true,
        partner_id: '',
    });
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
    
    // Company memberships state
    const [userMemberships, setUserMemberships] = useState([]);
    const [loadingMemberships, setLoadingMemberships] = useState(false);
    const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedMembershipRole, setSelectedMembershipRole] = useState('customer');
    const [isAddingCompany, setIsAddingCompany] = useState(false);
    const [removingMembershipId, setRemovingMembershipId] = useState(null);
    
    // Repro partners list for assignment
    const [reproPartners, setReproPartners] = useState([]);

    // Pending organization approvals moved to Companies page (2026-02).

    // Create user dialog state
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [createForm, setCreateForm] = useState({
        email: '',
        password: '',
        name: '',
        role: 'customer',
    });

    useEffect(() => {
        fetchUsers();
        fetchReproPartners();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await adminUsersApi.getAll();
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users');
        } finally {
            setIsLoading(false);
        }
    };
    
    const fetchReproPartners = async () => {
        try {
            const response = await partnersApi.list({ type: 'Repro', is_active: true });
            setReproPartners(response.data);
        } catch (error) {
            console.error('Failed to fetch repro partners:', error);
        }
    };

    // Create new user
    const handleCreateUser = async () => {
        // Validation
        if (!createForm.email || !createForm.password || !createForm.name) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        if (createForm.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setIsCreating(true);
        try {
            await adminUsersApi.create(createForm);
            toast.success(`User ${createForm.email} created successfully`);
            setIsCreateDialogOpen(false);
            setCreateForm({ email: '', password: '', name: '', role: 'customer' });
            setShowPassword(false);
            await fetchUsers();
        } catch (error) {
            console.error('Failed to create user:', error);
            toast.error(error.response?.data?.detail || 'Failed to create user');
        } finally {
            setIsCreating(false);
        }
    };

    // Open edit drawer
    const openEditDrawer = async (user) => {
        setSelectedUser(user);
        setEditForm({
            name: user.name || '',
            role: user.role,
            is_active: user.is_active !== false, // Default to true if undefined
            partner_id: user.partner_id || '',
        });
        setIsDrawerOpen(true);
        
        // Load audit logs
        setLoadingAuditLogs(true);
        try {
            const response = await adminUsersApi.getAuditLogs(user.id);
            setAuditLogs(response.data);
        } catch (error) {
            console.error('Failed to load audit logs:', error);
        } finally {
            setLoadingAuditLogs(false);
        }
        
        // Load user's company memberships
        fetchUserMemberships(user.id);
    };
    
    // Fetch company memberships for a user
    const fetchUserMemberships = async (userId) => {
        setLoadingMemberships(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/users/${userId}/companies`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUserMemberships(response.data?.memberships || []);
        } catch (error) {
            console.error('Failed to load memberships:', error);
            setUserMemberships([]);
        } finally {
            setLoadingMemberships(false);
        }
    };
    
    // Search companies to add user to
    const searchCompanies = async (query) => {
        if (!selectedUser) return;
        setIsSearchingCompanies(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/memberships/search/companies`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { user_id: selectedUser.id, q: query, exclude_memberships: true }
                }
            );
            setCompanySearchResults(response.data || []);
        } catch (error) {
            console.error('Failed to search companies:', error);
            toast.error('Failed to search companies');
        } finally {
            setIsSearchingCompanies(false);
        }
    };
    
    // Add user to a company
    const handleAddToCompany = async () => {
        if (!selectedUser || !selectedCompanyId) {
            toast.error('Please select a company');
            return;
        }
        setIsAddingCompany(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/memberships`,
                { 
                    user_id: selectedUser.id, 
                    company_id: selectedCompanyId, 
                    role: selectedMembershipRole,
                    is_primary: userMemberships.length === 0 // First company becomes primary
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Added user to company');
            setShowAddCompanyDialog(false);
            setSelectedCompanyId('');
            setCompanySearchQuery('');
            setCompanySearchResults([]);
            fetchUserMemberships(selectedUser.id);
            fetchUsers(); // Refresh main list to update organization name
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add user to company');
        } finally {
            setIsAddingCompany(false);
        }
    };
    
    // Remove user from a company
    const handleRemoveFromCompany = async (membership) => {
        if (!confirm(`Remove ${selectedUser?.name} from ${membership.company_name}?`)) return;
        setRemovingMembershipId(membership.id);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/memberships/${membership.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Removed from ${membership.company_name}`);
            fetchUserMemberships(selectedUser.id);
            fetchUsers(); // Refresh main list
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove from company');
        } finally {
            setRemovingMembershipId(null);
        }
    };
    
    // Set company as primary for user
    const handleSetPrimaryCompany = async (membership) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/memberships/${membership.id}`,
                { is_primary: true },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Set ${membership.company_name} as primary company`);
            fetchUserMemberships(selectedUser.id);
            fetchUsers(); // Refresh main list
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update');
        }
    };

    // Close drawer
    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedUser(null);
        setAuditLogs([]);
        setUserMemberships([]);
        setShowAddCompanyDialog(false);
        setCompanySearchQuery('');
        setCompanySearchResults([]);
    };

    // Save changes
    const handleSave = async () => {
        if (!selectedUser) return;
        
        setIsSaving(true);
        try {
            const updateData = {};
            
            // Only include changed fields
            if (editForm.name !== selectedUser.name) {
                updateData.name = editForm.name;
            }
            if (editForm.role !== selectedUser.role) {
                updateData.role = editForm.role;
            }
            if (editForm.is_active !== (selectedUser.is_active !== false)) {
                updateData.is_active = editForm.is_active;
            }
            
            // Handle partner_id change (for repro_partner users)
            const currentPartnerId = selectedUser.partner_id || '';
            if (editForm.partner_id !== currentPartnerId) {
                updateData.partner_id = editForm.partner_id || null;
            }
            
            // Clear partner_id if role is changed away from repro_partner
            if (editForm.role !== 'repro_partner' && selectedUser.partner_id) {
                updateData.partner_id = null;
            }
            
            if (Object.keys(updateData).length === 0) {
                toast.info('No changes to save');
                return;
            }
            
            await adminUsersApi.update(selectedUser.id, updateData);
            toast.success('User updated successfully');
            
            // Refresh users list
            await fetchUsers();
            closeDrawer();
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Failed to update user';
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    // Check if this is the current user (for preventing self-edit restrictions)
    const isCurrentUser = selectedUser?.id === currentUser?.id;
    
    // Filter, search, and sort users. Sort happens AFTER the
    // search/role filter so counts shown ("Showing N of M") are
    // not disrupted by sorting. ``last_login`` and ``created_at``
    // sort on the raw timestamp — not the formatted display text
    // — and "Never" (null last_login) always sinks to the bottom
    // regardless of direction.
    const filteredUsers = useMemo(() => {
        const base = users.filter(user => {
            if (roleFilter !== 'all' && user.role !== roleFilter) return false;
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                return (
                    user.name?.toLowerCase().includes(query) ||
                    user.email?.toLowerCase().includes(query)
                );
            }
            return true;
        });

        const dirMul = sortDir === 'asc' ? 1 : -1;
        const getKey = (u) => {
            switch (sortBy) {
                case 'name':
                    return (u.name || '').toLowerCase();
                case 'email':
                    return (u.email || '').toLowerCase();
                case 'role':
                    return (u.role || '').toLowerCase();
                case 'is_active':
                    // Active (true) sorts above inactive (false)
                    // when ascending; `false === 0` naturally
                    // ranks below `true === 1`.
                    return u.is_active === false ? 0 : 1;
                case 'last_login':
                    // Return null (NEVER) separately so we can
                    // sink those regardless of direction below.
                    return u.last_login ? new Date(u.last_login).getTime() : null;
                case 'organization_name':
                    return (u.organization_name || '').toLowerCase();
                case 'created_at':
                    return u.created_at ? new Date(u.created_at).getTime() : null;
                default:
                    return (u.name || '').toLowerCase();
            }
        };

        return [...base].sort((a, b) => {
            const va = getKey(a);
            const vb = getKey(b);
            // Nulls / empties always sink to the bottom regardless
            // of asc/desc — matches the "Never placed last" rule
            // from the spec and keeps empty organizations grouped
            // after real values.
            const aEmpty = va == null || va === '';
            const bEmpty = vb == null || vb === '';
            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return 1;
            if (bEmpty) return -1;
            if (va < vb) return -1 * dirMul;
            if (va > vb) return 1 * dirMul;
            // Stable tie-breaker by id so the order is deterministic.
            return (a.id || '').localeCompare(b.id || '');
        });
    }, [users, searchQuery, roleFilter, sortBy, sortDir]);

    // Header click handler — toggles direction on repeat clicks,
    // flips to a sensible default on first click per column.
    const toggleSort = (col) => {
        if (sortBy === col) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortBy(col);
        // Timestamps default to desc (newest first), everything
        // else defaults to asc.
        setSortDir(col === 'last_login' || col === 'created_at' ? 'desc' : 'asc');
    };

    // Reusable sortable header cell. Preserves the existing
    // column styling and i18n label while adding a click target
    // and a direction arrow on the active column.
    const SortIcon = ({ col }) => {
        if (sortBy !== col) {
            return <ChevronsUpDown className="h-3 w-3 text-slate-400 opacity-60" />;
        }
        return sortDir === 'asc'
            ? <ChevronUp className="h-3 w-3 text-slate-700" data-testid={`sort-arrow-${col}-asc`} />
            : <ChevronDown className="h-3 w-3 text-slate-700" data-testid={`sort-arrow-${col}-desc`} />;
    };

    // Count users by role
    const roleCounts = useMemo(() => {
        return users.reduce((acc, user) => {
            acc[user.role] = (acc[user.role] || 0) + 1;
            return acc;
        }, {});
    }, [users]);

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Compact "when did they last sign in?" label for the Users table.
    // - Today, 08:42
    // - Yesterday
    // - dd MMM yyyy for older
    // - Never when the user has never signed in
    // Reuses the existing users.last_login timestamp; no new tracking.
    const formatLastLogin = (dateString) => {
        if (!dateString) return t('users.last_login_never', 'Never');
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return t('users.last_login_never', 'Never');
        const now = new Date();
        const isSameDay = (a, b) =>
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();
        if (isSameDay(d, now)) {
            const hhmm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            return t('users.last_login_today', 'Today, {time}', { time: hhmm });
        }
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (isSameDay(d, yesterday)) return t('users.last_login_yesterday', 'Yesterday');
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Get audit log event label
    const getAuditEventLabel = (event) => {
        switch (event.event_type) {
            case 'role_changed':
                return `Role changed: ${event.old_value} → ${event.new_value}`;
            case 'user_disabled':
                return 'Account disabled';
            case 'user_enabled':
                return 'Account enabled';
            case 'user_updated':
                return `${event.field} updated`;
            default:
                return event.event_type;
        }
    };

    return (
        <div className={embedded ? '' : 'min-h-screen bg-background'}>
            {!embedded && <Header />}

            <main className={embedded ? 'space-y-4' : 'max-w-7xl mx-auto px-6 py-6'}>
                {/* Page Header */}
                <div className="mb-6 flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <Users className="h-6 w-6 text-indigo-600" />
                            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {t('users.title', 'Users')}
                            </h1>
                        </div>
                        <p className="text-sm text-slate-500">
                            {t('users.subtitle', 'Manage and view all users in the system')}
                        </p>
                    </div>
                    <Button
                        onClick={() => setIsCreateDialogOpen(true)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="create-user-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Create User
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg border border-border shadow-premium-sm p-4">
                        <div className="text-2xl font-bold text-slate-900">{users.length}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">{t('users.total', 'Total Users')}</div>
                    </div>
                    <div className="bg-white rounded-lg border border-border shadow-premium-sm p-4">
                        <div className="text-2xl font-bold text-indigo-600">{roleCounts.admin || 0}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">{t('users.admins', 'Admins')}</div>
                    </div>
                    <div className="bg-white rounded-lg border border-border shadow-premium-sm p-4">
                        <div className="text-2xl font-bold text-slate-600">{roleCounts.customer || 0}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">{t('users.customers', 'Customers')}</div>
                    </div>
                    <div className="bg-white rounded-lg border border-border shadow-premium-sm p-4">
                        <div className="text-2xl font-bold text-purple-600">{roleCounts.translator || 0}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">{t('users.translators', 'Translators')}</div>
                    </div>
                </div>

                {/* Pending Organization Approvals moved to Companies
                    (2026-02) — approval + e-conomic sync belongs with
                    the billing profile, not the person/login. */}

                {/* Filters */}
                <div className="bg-white rounded-lg border border-border shadow-premium-sm p-4 mb-4">
                    <div className="flex items-center gap-4">
                        {/* Search */}
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                                type="text"
                                placeholder={t('users.search_placeholder', 'Search by name or email...')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 h-9"
                                data-testid="users-search-input"
                            />
                        </div>

                        {/* Role Filter */}
                        <div className="flex items-center gap-2">
                            <Filter className="h-4 w-4 text-slate-400" />
                            <Select value={roleFilter} onValueChange={setRoleFilter}>
                                <SelectTrigger className="w-[150px] h-9" data-testid="users-role-filter">
                                    <SelectValue placeholder="Filter by role" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('users.all_roles', 'All Roles')}</SelectItem>
                                    <SelectItem value="admin">{t('users.role_admin', 'Admin')}</SelectItem>
                                    <SelectItem value="customer">{t('users.role_customer', 'Customer')}</SelectItem>
                                    <SelectItem value="translator">{t('users.role_translator', 'Translator')}</SelectItem>
                                    <SelectItem value="repro_partner">Repro Partner</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Results count */}
                        <div className="text-sm text-slate-500 ml-auto">
                            {t('users.showing', 'Showing')} <span className="font-medium text-slate-700">{filteredUsers.length}</span> {t('users.of', 'of')} {users.length} {t('users.users', 'users')}
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg border border-border shadow-premium-sm overflow-hidden">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            {searchQuery || roleFilter !== 'all' 
                                ? t('users.no_results', 'No users match your search criteria')
                                : t('users.no_users', 'No users found')
                            }
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-background">
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('name')}
                                        data-testid="users-sort-name"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_name', 'Name')}
                                            <SortIcon col="name" />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('email')}
                                        data-testid="users-sort-email"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_email', 'Email')}
                                            <SortIcon col="email" />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('role')}
                                        data-testid="users-sort-role"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_role', 'Role')}
                                            <SortIcon col="role" />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('is_active')}
                                        data-testid="users-sort-status"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_status', 'Status')}
                                            <SortIcon col="is_active" />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('last_login')}
                                        data-testid="users-sort-last-login"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_last_login', 'Last Login')}
                                            <SortIcon col="last_login" />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('organization_name')}
                                        data-testid="users-sort-organization"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_organization', 'Organization')}
                                            <SortIcon col="organization_name" />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        className="text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-slate-800"
                                        onClick={() => toggleSort('created_at')}
                                        data-testid="users-sort-created"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {t('users.col_created', 'Created')}
                                            <SortIcon col="created_at" />
                                        </span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((user) => (
                                    <TableRow 
                                        key={user.id} 
                                        className={`hover:bg-background cursor-pointer ${user.is_active === false ? 'opacity-60' : ''}`}
                                        onClick={() => navigate(`/users/${user.id}`)}
                                        data-testid={`user-row-${user.id}`}
                                    >
                                        <TableCell className="py-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                    user.is_active === false 
                                                        ? 'bg-slate-200 text-slate-400' 
                                                        : 'bg-slate-200 text-slate-600'
                                                }`}>
                                                    {user.name?.charAt(0).toUpperCase() || '?'}
                                                </div>
                                                <span className="font-medium text-slate-900">{user.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 text-sm text-slate-600">
                                            {user.email}
                                        </TableCell>
                                        <TableCell className="py-3">
                                            <RoleBadge role={user.role} />
                                        </TableCell>
                                        <TableCell className="py-3">
                                            <StatusBadge isActive={user.is_active !== false} />
                                        </TableCell>
                                        <TableCell
                                            className="py-3 text-sm text-slate-600 whitespace-nowrap"
                                            data-testid={`user-last-login-${user.id}`}
                                            title={user.last_login ? formatDateTime(user.last_login) : t('users.last_login_never', 'Never')}
                                        >
                                            {formatLastLogin(user.last_login)}
                                        </TableCell>
                                        <TableCell className="py-3 text-sm text-slate-600">
                                            <div className="flex items-center gap-2">
                                                {user.organization_id ? (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/admin/companies/${user.organization_id}`);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {user.organization_name || 'Unnamed'}
                                                    </button>
                                                ) : (
                                                    <span>—</span>
                                                )}
                                                {user.organization_billing_status === 'synced_to_economic' && (
                                                    <span 
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700"
                                                        title={`e-conomic ID: ${user.organization_economic_id || 'N/A'}`}
                                                        data-testid={`economic-badge-${user.id}`}
                                                    >
                                                        <CheckCircle2 className="h-3 w-3" />
                                                        e-conomic
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 text-sm text-slate-500">
                                            {formatDate(user.created_at)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </main>

            {/* Edit User Drawer */}
            <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                <SheetContent className="w-[450px] sm:w-[500px] overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-2">
                            <User className="h-5 w-5" />
                            Edit User
                        </SheetTitle>
                        <SheetDescription>
                            Update user details, role, and status
                        </SheetDescription>
                    </SheetHeader>

                    {selectedUser && (
                        <div className="mt-6 space-y-6">
                            {/* Read-only Info */}
                            <div className="space-y-3 p-4 bg-background rounded-lg">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    User Information (Read-only)
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Hash className="h-4 w-4 text-slate-400" />
                                        <span className="text-slate-500">ID:</span>
                                        <span className="font-mono text-xs text-slate-600">{selectedUser.id}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="h-4 w-4 text-slate-400" />
                                        <span className="text-slate-500">Email:</span>
                                        <span className="text-slate-700">{selectedUser.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Calendar className="h-4 w-4 text-slate-400" />
                                        <span className="text-slate-500">Created:</span>
                                        <span className="text-slate-700">{formatDateTime(selectedUser.created_at)}</span>
                                    </div>
                                    {selectedUser.last_login && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Clock className="h-4 w-4 text-slate-400" />
                                            <span className="text-slate-500">Last login:</span>
                                            <span className="text-slate-700">{formatDateTime(selectedUser.last_login)}</span>
                                        </div>
                                    )}
                                    {selectedUser.organization_name && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Users className="h-4 w-4 text-slate-400" />
                                            <span className="text-slate-500">Organization:</span>
                                            <span className="text-slate-700">{selectedUser.organization_name}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Editable Fields */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Editable Fields
                                </h4>

                                {/* Display Name */}
                                <div className="space-y-2">
                                    <Label htmlFor="name">Display Name</Label>
                                    <Input
                                        id="name"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        placeholder="Enter display name"
                                        data-testid="edit-user-name"
                                    />
                                </div>

                                {/* Role */}
                                <div className="space-y-2">
                                    <Label htmlFor="role">Role</Label>
                                    {isCurrentUser ? (
                                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                                            <AlertTriangle className="h-4 w-4" />
                                            <span>You cannot change your own role</span>
                                        </div>
                                    ) : (
                                        <Select
                                            value={editForm.role}
                                            onValueChange={(value) => {
                                                setEditForm({ ...editForm, role: value, partner_id: value !== 'repro_partner' ? '' : editForm.partner_id });
                                            }}
                                        >
                                            <SelectTrigger data-testid="edit-user-role">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="customer">Customer</SelectItem>
                                                <SelectItem value="admin">Admin</SelectItem>
                                                <SelectItem value="translator">Translator</SelectItem>
                                                <SelectItem value="repro_partner">Repro Partner</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>

                                {/* Partner Assignment (only for repro_partner role) */}
                                {editForm.role === 'repro_partner' && !isCurrentUser && (
                                    <div className="space-y-2">
                                        <Label htmlFor="partner">Linked Partner</Label>
                                        <Select
                                            value={editForm.partner_id}
                                            onValueChange={(value) => setEditForm({ ...editForm, partner_id: value })}
                                        >
                                            <SelectTrigger data-testid="edit-user-partner">
                                                <SelectValue placeholder="Select a repro partner..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {reproPartners.length === 0 ? (
                                                    <div className="px-2 py-3 text-sm text-slate-500 text-center">
                                                        No repro partners available.<br />
                                                        <span className="text-xs">Create one in Settings → Partners</span>
                                                    </div>
                                                ) : (
                                                    reproPartners.map((partner) => (
                                                        <SelectItem key={partner.id} value={partner.id}>
                                                            {partner.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">
                                            Link this user to a repro partner to enable them to view and work on jobs assigned to that partner.
                                        </p>
                                    </div>
                                )}

                                {/* Status */}
                                <div className="space-y-2">
                                    <Label>Account Status</Label>
                                    {isCurrentUser ? (
                                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                                            <AlertTriangle className="h-4 w-4" />
                                            <span>You cannot disable your own account</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full ${editForm.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                                <span className="text-sm font-medium">
                                                    {editForm.is_active ? 'Active' : 'Disabled'}
                                                </span>
                                            </div>
                                            <Switch
                                                checked={editForm.is_active}
                                                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                                                data-testid="edit-user-status"
                                            />
                                        </div>
                                    )}
                                    {!editForm.is_active && (
                                        <p className="text-xs text-slate-500">
                                            Disabled users cannot log in but retain all historical data.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Company Memberships Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                                        <Building2 className="h-3 w-3" />
                                        Company Memberships
                                    </h4>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setShowAddCompanyDialog(true);
                                            searchCompanies('');
                                        }}
                                        className="h-7 text-xs"
                                        data-testid="add-company-membership-btn"
                                    >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Add Company
                                    </Button>
                                </div>
                                
                                {loadingMemberships ? (
                                    <div className="text-center py-4 text-sm text-slate-400">Loading...</div>
                                ) : userMemberships.length === 0 ? (
                                    <div className="text-center py-4 text-sm text-slate-400 bg-background rounded-lg">
                                        No company memberships
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {userMemberships.map((membership) => (
                                            <div 
                                                key={membership.id} 
                                                className="flex items-center justify-between p-3 bg-background rounded-lg"
                                                data-testid={`membership-${membership.company_id}`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-slate-700 text-sm truncate">
                                                                {membership.company_name}
                                                            </span>
                                                            {membership.is_primary && (
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 flex items-center gap-0.5">
                                                                    <Star className="h-2.5 w-2.5" />
                                                                    Primary
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-slate-400 capitalize">
                                                            {membership.role}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {!membership.is_primary && (
                                                        <button
                                                            onClick={() => handleSetPrimaryCompany(membership)}
                                                            className="p-1 text-slate-400 hover:text-amber-600 transition-colors"
                                                            title="Set as primary company"
                                                        >
                                                            <Star className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleRemoveFromCompany(membership)}
                                                        disabled={removingMembershipId === membership.id}
                                                        className="p-1 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                                        title="Remove from company"
                                                    >
                                                        {removingMembershipId === membership.id ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        ) : (
                                                            <X className="h-3.5 w-3.5" />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Audit Log */}
                            <div className="space-y-3">
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                                    <History className="h-3 w-3" />
                                    Recent Changes
                                </h4>
                                {loadingAuditLogs ? (
                                    <div className="text-center py-4 text-sm text-slate-400">Loading...</div>
                                ) : auditLogs.length === 0 ? (
                                    <div className="text-center py-4 text-sm text-slate-400">No changes recorded</div>
                                ) : (
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {auditLogs.slice(0, 10).map((log) => (
                                            <div key={log.id} className="text-xs bg-background rounded p-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-slate-700">
                                                        {getAuditEventLabel(log)}
                                                    </span>
                                                    <span className="text-slate-400">
                                                        {formatDateTime(log.created_at)}
                                                    </span>
                                                </div>
                                                <p className="text-slate-500 mt-0.5">by {log.admin_name}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    onClick={closeDrawer}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex-1"
                                    data-testid="save-user-btn"
                                >
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
            
            {/* Create User Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="h-5 w-5 text-indigo-600" />
                            Create New User
                        </DialogTitle>
                        <DialogDescription>
                            Add a new user to the system. They will receive login credentials.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="create-name">Name <span className="text-red-500">*</span></Label>
                            <Input
                                id="create-name"
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                placeholder="Enter user's name"
                                data-testid="create-user-name"
                            />
                        </div>
                        
                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="create-email">Email <span className="text-red-500">*</span></Label>
                            <Input
                                id="create-email"
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                placeholder="user@example.com"
                                data-testid="create-user-email"
                            />
                        </div>
                        
                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="create-password">Password <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Input
                                    id="create-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={createForm.password}
                                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                    placeholder="Minimum 6 characters"
                                    className="pr-10"
                                    data-testid="create-user-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-slate-500">Password must be at least 6 characters</p>
                        </div>
                        
                        {/* Role */}
                        <div className="space-y-2">
                            <Label htmlFor="create-role">Role</Label>
                            <Select
                                value={createForm.role}
                                onValueChange={(value) => setCreateForm({ ...createForm, role: value })}
                            >
                                <SelectTrigger data-testid="create-user-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="customer">Customer</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="super_admin">Super Admin</SelectItem>
                                    <SelectItem value="translator">Translator</SelectItem>
                                    <SelectItem value="repro_partner">Repro Partner</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsCreateDialogOpen(false);
                                setCreateForm({ email: '', password: '', name: '', role: 'customer' });
                                setShowPassword(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateUser}
                            disabled={isCreating}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="create-user-submit"
                        >
                            {isCreating ? 'Creating...' : 'Create User'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Add Company Membership Dialog */}
            <Dialog open={showAddCompanyDialog} onOpenChange={setShowAddCompanyDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-indigo-600" />
                            Add to Company
                        </DialogTitle>
                        <DialogDescription>
                            Add {selectedUser?.name} to a company
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Company Search */}
                        <div className="space-y-2">
                            <Label>Search Company</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    placeholder="Search by company name..."
                                    value={companySearchQuery}
                                    onChange={(e) => {
                                        setCompanySearchQuery(e.target.value);
                                        searchCompanies(e.target.value);
                                    }}
                                    className="pl-9"
                                    data-testid="search-company-input"
                                />
                            </div>
                        </div>
                        
                        {/* Company Results */}
                        <div className="space-y-2">
                            <Label>Select Company</Label>
                            {isSearchingCompanies ? (
                                <div className="text-center py-4 text-sm text-slate-400">
                                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                    Searching...
                                </div>
                            ) : companySearchResults.length === 0 ? (
                                <div className="text-center py-4 text-sm text-slate-400 bg-background rounded-lg">
                                    {companySearchQuery ? 'No companies found' : 'Enter company name to search'}
                                </div>
                            ) : (
                                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                                    {companySearchResults.map((company) => (
                                        <label
                                            key={company.id}
                                            className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                                                selectedCompanyId === company.id 
                                                    ? 'bg-indigo-50' 
                                                    : 'hover:bg-slate-50'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="company"
                                                checked={selectedCompanyId === company.id}
                                                onChange={() => setSelectedCompanyId(company.id)}
                                                className="rounded-full border-slate-300"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-slate-900 text-sm truncate">{company.name}</p>
                                                <p className="text-xs text-slate-400 truncate">{company.id}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Membership Role */}
                        <div className="space-y-2">
                            <Label>Membership Role</Label>
                            <Select value={selectedMembershipRole} onValueChange={setSelectedMembershipRole}>
                                <SelectTrigger data-testid="add-membership-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="customer">Customer</SelectItem>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="owner">Owner</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Role within this company (separate from system role)
                            </p>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAddCompanyDialog(false);
                                setSelectedCompanyId('');
                                setCompanySearchQuery('');
                                setCompanySearchResults([]);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddToCompany}
                            disabled={!selectedCompanyId || isAddingCompany}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="confirm-add-company-btn"
                        >
                            {isAddingCompany ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Adding...
                                </>
                            ) : (
                                'Add to Company'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
